/* global Set */
import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

import ErrorMessage from "components/UI/ErrorMessage"
import { event } from "lib/gtag"
import {
  ATTEMPT_MODES,
  PLAYER_PHASES,
  isAutoplayViewportEligible,
  isIdlePausedPlayer,
  nextAttemptAfterFailure,
  providerErrorAction,
  shouldAutoplay,
  shouldShowPausedPlayer,
} from "lib/videoAutoplayPolicy"
import LiteYouTubeEmbed from "./LiteYouTubeEmbed"
import LiteVimeoEmbed from "./LiteVimeoEmbed"

const VIMEO_URL = /vimeo\.com\/.+/
const PLAYER_READY_TIMEOUT_MS = 12000
const PLAYER_PLAY_TIMEOUT_MS = 3000

const initialPlayerState = {
  active: false,
  attemptMode: null,
  error: null,
  phase: PLAYER_PHASES.POSTER,
  playerKey: 0,
  playing: false,
}

const createEventState = () => ({
  autoplayBlocked: false,
  autoplayStarted: false,
  end: false,
  ready: false,
  start: false,
  thresholds: new Set(),
  userInteraction: false,
})

const VideoPlayer = (props) => {
  const [playerState, setPlayerStateValue] = useState(initialPlayerState)
  const [duration, setDuration] = useState(0)
  const playerStateRef = useRef(initialPlayerState)
  const playerContainerRef = useRef(null)
  const autoplayViewportEligibleRef = useRef(false)
  const autoplayViewportObserverRef = useRef(null)
  const readyTimeoutRef = useRef(null)
  const playTimeoutRef = useRef(null)
  const failAttemptRef = useRef(function () {})
  const mountedRef = useRef(false)
  const pageInactiveRef = useRef(false)
  const connectionRef = useRef(null)
  const eventStateRef = useRef(createEventState())
  const isVimeo = VIMEO_URL.test(props.url)

  const setPlayerState = (value) => {
    const previous = playerStateRef.current
    const next = typeof value === "function" ? value(previous) : value
    playerStateRef.current = next
    setPlayerStateValue(next)
    return next
  }

  const isCurrentAttempt = (attemptId) =>
    mountedRef.current &&
    playerStateRef.current.active &&
    playerStateRef.current.playerKey === attemptId

  const clearAttemptTimeouts = () => {
    clearTimeout(readyTimeoutRef.current)
    clearTimeout(playTimeoutRef.current)
    readyTimeoutRef.current = null
    playTimeoutRef.current = null
  }

  const stopAutoplayViewportObservation = () => {
    if (autoplayViewportObserverRef.current) {
      autoplayViewportObserverRef.current.disconnect()
      autoplayViewportObserverRef.current = null
    }
  }

  const sendAutoplayBlocked = () => {
    if (eventStateRef.current.autoplayBlocked) return
    eventStateRef.current.autoplayBlocked = true
    event({
      action: "Autoplay_Blocked",
      category: "Play",
      label: props.url,
      interaction: false,
    })
  }

  const startReadyTimeout = (attemptId) => {
    clearAttemptTimeouts()
    readyTimeoutRef.current = setTimeout(() => {
      if (!isCurrentAttempt(attemptId)) return
      failAttemptRef.current(new Error("Player readiness timed out"))
    }, PLAYER_READY_TIMEOUT_MS)
  }

  const beginAttempt = ({ manual, attemptMode }) => {
    clearAttemptTimeouts()
    const next = setPlayerState((current) => ({
      active: true,
      attemptMode,
      error: null,
      phase: manual
        ? PLAYER_PHASES.MANUAL_PLAY_REQUIRED
        : PLAYER_PHASES.AUTOPLAY_ATTEMPTING,
      playerKey: current.playerKey + 1,
      playing: true,
    }))
    startReadyTimeout(next.playerKey)
  }

  // 止めたプレーヤーを置く時の key。再生を試して始まっていないプレーヤーは、
  // 埋め込みに自動再生が入ったままなので、作り直して自動再生なしにする
  const pausedPlayerKey = (current) =>
    !current.active ||
    (current.playing && current.phase !== PLAYER_PHASES.PLAYING)
      ? current.playerKey + 1
      : current.playerKey

  const showManualPlay = ({ providerError = false } = {}) => {
    stopAutoplayViewportObservation()
    clearAttemptTimeouts()
    const keepPlayer = shouldShowPausedPlayer({
      connection: connectionRef.current,
      providerError,
    })
    setPlayerState((current) => ({
      ...current,
      active: keepPlayer,
      attemptMode: null,
      error: null,
      phase: PLAYER_PHASES.MANUAL_PLAY_REQUIRED,
      playerKey: keepPlayer ? pausedPlayerKey(current) : current.playerKey,
      playing: false,
    }))
  }

  const showProviderError = (error) => {
    clearAttemptTimeouts()
    setPlayerState((current) => ({
      ...current,
      active: false,
      error: error || new Error("Unable to play video"),
      phase: PLAYER_PHASES.PROVIDER_ERROR,
      playing: false,
    }))
  }

  const failAttempt = (error, { providerError = false } = {}) => {
    const current = playerStateRef.current
    const decision = nextAttemptAfterFailure({
      attemptMode: current.attemptMode,
    })

    clearAttemptTimeouts()

    if (current.attemptMode === ATTEMPT_MODES.AUTOPLAY) {
      sendAutoplayBlocked()
    }

    if (decision.action === "provider-error") {
      showProviderError(error)
      return
    }
    showManualPlay({ providerError })
  }
  failAttemptRef.current = failAttempt

  useEffect(() => {
    mountedRef.current = true
    pageInactiveRef.current = document.hidden
    setDuration(0)
    eventStateRef.current = createEventState()

    const motionPreference = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    connectionRef.current = connection || null
    let viewportObserver = null
    let autoplayDecisionMade = false
    let autoplayAttemptStarted = false
    autoplayViewportEligibleRef.current = false

    const autoplayEnabled = shouldAutoplay({
      reducedMotion: Boolean(motionPreference && motionPreference.matches),
      connection,
    })
    if (
      autoplayEnabled &&
      typeof window.IntersectionObserver === "function" &&
      playerContainerRef.current
    ) {
      viewportObserver = new window.IntersectionObserver(
        (entries) => {
          const viewportEligible = entries.some(isAutoplayViewportEligible)
          autoplayViewportEligibleRef.current = viewportEligible

          if (
            autoplayDecisionMade ||
            pageInactiveRef.current ||
            document.hidden ||
            eventStateRef.current.userInteraction
          ) {
            return
          }

          if (!viewportEligible) {
            if (autoplayAttemptStarted) {
              autoplayDecisionMade = true
              sendAutoplayBlocked()
              showManualPlay()
            }
            return
          }
          if (autoplayAttemptStarted) return

          autoplayAttemptStarted = true
          beginAttempt({
            manual: false,
            attemptMode: ATTEMPT_MODES.AUTOPLAY,
          })
        },
        { threshold: 0.51 },
      )
      autoplayViewportObserverRef.current = viewportObserver
      viewportObserver.observe(playerContainerRef.current)
    } else {
      showManualPlay()
    }

    const stopForMotionPreference = (change) => {
      if (!change.matches) return
      autoplayDecisionMade = true
      showManualPlay()
    }
    if (motionPreference) {
      if (motionPreference.addEventListener) {
        motionPreference.addEventListener("change", stopForMotionPreference)
      } else if (motionPreference.addListener) {
        motionPreference.addListener(stopForMotionPreference)
      }
    }

    return () => {
      mountedRef.current = false
      playerStateRef.current = {
        ...playerStateRef.current,
        active: false,
        playing: false,
      }
      clearAttemptTimeouts()
      stopAutoplayViewportObservation()
      if (motionPreference) {
        if (motionPreference.removeEventListener) {
          motionPreference.removeEventListener(
            "change",
            stopForMotionPreference,
          )
        } else if (motionPreference.removeListener) {
          motionPreference.removeListener(stopForMotionPreference)
        }
      }
    }
  }, [props.url])

  useEffect(() => {
    const pauseForHiddenPage = (browserEvent) => {
      if (!document.hidden && browserEvent.type !== "pagehide") {
        pageInactiveRef.current = false
        return
      }
      pageInactiveRef.current = true
      clearAttemptTimeouts()
      setPlayerState((current) => {
        // 止めて置いたプレーヤーはそのまま（PAUSED にすると、配信元のエラーで外れてしまう）
        if (!current.active || isIdlePausedPlayer(current)) return current
        if (current.phase === PLAYER_PHASES.AUTOPLAY_ATTEMPTING) {
          const keepPlayer = shouldShowPausedPlayer({
            connection: connectionRef.current,
          })
          return {
            ...current,
            active: keepPlayer,
            attemptMode: null,
            phase: PLAYER_PHASES.MANUAL_PLAY_REQUIRED,
            playerKey: keepPlayer
              ? pausedPlayerKey(current)
              : current.playerKey,
            playing: false,
          }
        }
        return {
          ...current,
          phase: PLAYER_PHASES.PAUSED,
          playing: false,
        }
      })
    }
    const restorePageActivity = () => {
      pageInactiveRef.current = false
    }
    document.addEventListener("visibilitychange", pauseForHiddenPage)
    window.addEventListener("pagehide", pauseForHiddenPage)
    window.addEventListener("pageshow", restorePageActivity)
    return () => {
      document.removeEventListener("visibilitychange", pauseForHiddenPage)
      window.removeEventListener("pagehide", pauseForHiddenPage)
      window.removeEventListener("pageshow", restorePageActivity)
    }
  }, [])

  const onReady = (attemptId) => {
    if (!isCurrentAttempt(attemptId)) return
    if (pageInactiveRef.current || document.hidden) {
      clearAttemptTimeouts()
      return
    }
    const current = playerStateRef.current
    clearTimeout(readyTimeoutRef.current)
    readyTimeoutRef.current = null
    clearTimeout(playTimeoutRef.current)
    playTimeoutRef.current = null
    if (
      current.playing &&
      (current.phase === PLAYER_PHASES.AUTOPLAY_ATTEMPTING ||
        current.phase === PLAYER_PHASES.MANUAL_PLAY_REQUIRED)
    ) {
      playTimeoutRef.current = setTimeout(() => {
        if (!isCurrentAttempt(attemptId)) return
        failAttemptRef.current(new Error("Playback start timed out"))
      }, PLAYER_PLAY_TIMEOUT_MS)
    }

    if (!eventStateRef.current.ready) {
      eventStateRef.current.ready = true
      event({
        action: "Ready",
        category: "Play",
        label: props.url,
        interaction: false,
      })
    }
  }

  const onStart = (attemptId) => {
    if (!isCurrentAttempt(attemptId) || pageInactiveRef.current) return
    if (!eventStateRef.current.start) {
      eventStateRef.current.start = true
      event({
        action: "Start",
        category: "Play",
        label: props.url,
        interaction: eventStateRef.current.userInteraction,
      })
    }
  }

  const onPlay = (attemptId) => {
    if (!isCurrentAttempt(attemptId)) return
    if (pageInactiveRef.current || document.hidden) {
      clearAttemptTimeouts()
      setPlayerState((current) => ({
        ...current,
        phase: PLAYER_PHASES.PAUSED,
        playing: false,
      }))
      return
    }

    clearAttemptTimeouts()
    const current = playerStateRef.current
    const isAutoplayAttempt = current.attemptMode === ATTEMPT_MODES.AUTOPLAY
    if (isAutoplayAttempt && !autoplayViewportEligibleRef.current) {
      sendAutoplayBlocked()
      showManualPlay()
      return
    }
    if (isAutoplayAttempt) stopAutoplayViewportObservation()

    if (current.phase === PLAYER_PHASES.PAUSED || !current.playing) {
      eventStateRef.current.userInteraction = true
    }
    setPlayerState({
      ...current,
      phase: PLAYER_PHASES.PLAYING,
      playing: true,
    })

    if (
      !eventStateRef.current.autoplayStarted &&
      current.attemptMode === ATTEMPT_MODES.AUTOPLAY
    ) {
      eventStateRef.current.autoplayStarted = true
      event({
        action: "Autoplay_Start",
        category: "Play",
        label: props.url,
        interaction: false,
      })
    }
  }

  const onPause = (attemptId) => {
    if (!isCurrentAttempt(attemptId)) return
    setPlayerState((current) => ({
      ...current,
      phase: PLAYER_PHASES.PAUSED,
      playing: false,
    }))
  }

  const onEnded = (attemptId) => {
    if (!isCurrentAttempt(attemptId)) return
    clearAttemptTimeouts()
    if (!eventStateRef.current.end) {
      eventStateRef.current.end = true
      event({
        action: "End",
        category: "Play",
        label: props.url,
        interaction: eventStateRef.current.userInteraction,
      })
    }
    setPlayerState((current) => ({
      ...current,
      phase: PLAYER_PHASES.PAUSED,
      playing: false,
    }))
  }

  const onDuration = (attemptId, videoDuration) => {
    if (!isCurrentAttempt(attemptId)) return
    setDuration(videoDuration)
  }

  const sendThresholdEvent = (threshold, action) => {
    if (eventStateRef.current.thresholds.has(threshold)) return
    eventStateRef.current.thresholds.add(threshold)
    event({
      action,
      category: "Play",
      label: props.url,
      interaction: eventStateRef.current.userInteraction,
    })
  }

  const onProgress = (attemptId, { playedSeconds }) => {
    if (!isCurrentAttempt(attemptId)) return
    if (!Number.isFinite(playedSeconds)) return
    if (playedSeconds >= 5) sendThresholdEvent("5", "5_Sec")
    if (playedSeconds >= 15) sendThresholdEvent("15", "15_Sec")
    if (playedSeconds >= 30) sendThresholdEvent("30", "30_Sec")
    if (duration > 0 && playedSeconds >= duration / 2) {
      sendThresholdEvent("half", "Half")
    }
  }

  const hasProviderFrame = () =>
    Boolean(
      playerContainerRef.current &&
        playerContainerRef.current.querySelector("iframe"),
    )

  const onProviderError = (attemptId, error) => {
    if (!isCurrentAttempt(attemptId)) return
    const current = playerStateRef.current
    const decision = providerErrorAction({
      phase: current.phase,
      attemptMode: current.attemptMode,
      hasProviderFrame: hasProviderFrame(),
    })
    if (decision.action === "keep") return
    if (decision.action === "attempt-failed") {
      // 自動再生の途中のエラーは「自動再生が始まらなかった」として扱う（Vimeo は自動再生を
      // 止められた時もエラーで返す）。iframe ができていない止めたプレーヤーは、サムネイルに戻す
      failAttemptRef.current(error, {
        providerError: current.attemptMode !== ATTEMPT_MODES.AUTOPLAY,
      })
      return
    }
    showProviderError(error)
  }

  const onManualPlay = () => {
    stopAutoplayViewportObservation()
    eventStateRef.current.userInteraction = true
    beginAttempt({
      manual: true,
      attemptMode: ATTEMPT_MODES.MANUAL,
    })
  }

  const createErrorMessage = (error) => {
    if (error === null || error === undefined) return "Unable to play video."
    if (typeof error === "number" || typeof error === "string") {
      return `Unable to play video. Error: ${error}`
    }
    if (error.message) return error.message
    return String(error)
  }

  const Embed = isVimeo ? LiteVimeoEmbed : LiteYouTubeEmbed
  const attemptId = playerState.playerKey

  return (
    <div
      className={classNames(props.className, "bg-base-indented-light")}
      data-testid="video-player"
      data-player-phase={playerState.phase}>
      <div className="mx-auto box-x-wrapper lg:max-w-lg">
        <div
          ref={playerContainerRef}
          className="relative w-full aspect-ratio-16/9">
          <div className="absolute inset-0">
            <input type="hidden" name="murl" value={props.url} />
            <div className="h-full player-wrapper">
              {playerState.error ? (
                <ErrorMessage
                  className="w-full h-full"
                  title="It's gone flat"
                  description={createErrorMessage(playerState.error)}
                  reloadButton
                  homeButton
                />
              ) : (
                <Embed
                  active={playerState.active}
                  className="absolute inset-0 z-10"
                  url={props.url}
                  title={props.title}
                  poster={props.poster}
                  playing={playerState.playing}
                  playerKey={playerState.playerKey}
                  onActivate={onManualPlay}
                  onReady={() => onReady(attemptId)}
                  onStart={() => onStart(attemptId)}
                  onPlay={() => onPlay(attemptId)}
                  onPause={() => onPause(attemptId)}
                  onEnded={() => onEnded(attemptId)}
                  onDuration={(videoDuration) =>
                    onDuration(attemptId, videoDuration)
                  }
                  onProgress={(progress) => onProgress(attemptId, progress)}
                  onError={(error) => onProviderError(attemptId, error)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

VideoPlayer.propTypes = {
  className: PropTypes.string,
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  poster: PropTypes.string.isRequired,
}

export default VideoPlayer
